/**
 * "Conversations" on a customer or a job page (tracker item 56): the
 * conversations with the customer (or about the job), each linking to its
 * thread in the Inbox, and "Start conversation" (an email to the customer).
 * Shown only to roles the company's inboxAccess setting lets into the inbox;
 * a 403 from the server hides the section.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { createAPIClient, ICompanySettingsApi, IConversationsApi } from '../api';
import * as types from '../api/types';
import { useAuth } from '../context/AuthContext';
import { getErrorCode } from '../utils/apiError';
import { secondaryButtonClass } from './formStyles';
import { showToast } from './Toast';
import { NewConversationForm } from '../pages/inbox/NewConversationForm';
import {
  CONVERSATION_STATUS_LABELS,
  conversationErrorMessage,
  conversationStatusClass,
  formatWhen,
  inboxAllowed,
  inboxFilterPath,
  inboxThreadPath,
} from '../pages/inbox/inboxUi';

interface Props {
  customer: { id: string; name: string };
  /** On a job page: only that job's conversations, and new ones are linked to it */
  jobId?: string;
}

export function ConversationsSection({ customer, jobId }: Props) {
  const api = useMemo<IConversationsApi & ICompanySettingsApi>(() => createAPIClient(), []);
  const { user } = useAuth();
  const navigate = useNavigate();
  const [allowed, setAllowed] = useState<boolean>(inboxAllowed(user?.role, undefined));
  const [rows, setRows] = useState<types.Conversation[] | null>(null);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  // A technician sees the section only when the company opened the inbox to everyone
  useEffect(() => {
    if (user?.role !== types.UserRole.TECHNICIAN) return undefined;
    let active = true;
    void (async () => {
      try {
        const settings = await api.getCompanySettings();
        if (active) setAllowed(inboxAllowed(user.role, settings.inboxAccess));
      } catch {
        if (active) setAllowed(false);
      }
    })();
    return () => { active = false; };
  }, [api, user?.role]);

  const load = useCallback(async () => {
    setError(null);
    try {
      const result = await api.listConversations(jobId ? { jobId, limit: 10 } : { customerId: customer.id, limit: 10 });
      setRows(result.data);
      setTotal(result.pagination.total);
    } catch (cause) {
      // Not allowed, or no answer from the server at all: the section is secondary, hide it
      if (getErrorCode(cause) === 'INBOX_ACCESS_DENIED' || !(axios.isAxiosError(cause) && cause.response)) setAllowed(false);
      else setError(conversationErrorMessage(cause, 'Could not load conversations'));
    }
  }, [api, customer.id, jobId]);

  useEffect(() => {
    if (allowed) void load();
  }, [allowed, load]);

  if (!allowed) return null;

  return (
    <section aria-labelledby="conversations-heading" className="rounded-lg bg-white p-4 shadow sm:p-6" data-testid="conversations-section">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 id="conversations-heading" className="text-lg font-semibold text-gray-900">Conversations</h2>
        {!creating && <button type="button" className={secondaryButtonClass} onClick={() => setCreating(true)}>Start conversation</button>}
      </div>
      {creating && (
        <div className="mt-3">
          <NewConversationForm
            customer={customer}
            jobId={jobId ?? null}
            onCancel={() => setCreating(false)}
            onCreated={(created) => {
              setCreating(false);
              showToast('success', 'Email queued');
              navigate(inboxThreadPath(created.conversation.id));
            }}
          />
        </div>
      )}
      {error && <p role="alert" className="mt-3 rounded bg-red-50 p-3 text-sm text-red-800">{error}</p>}
      {rows && rows.length === 0 && <p className="mt-3 text-sm text-gray-600">No conversations yet.</p>}
      {rows && rows.length > 0 && (
        <ul className="mt-3 divide-y divide-gray-100" aria-label="Conversations">
          {rows.map((c) => (
            <li key={c.id} className="flex flex-wrap items-center justify-between gap-2 py-2">
              <Link to={inboxThreadPath(c.id)} className={`min-w-0 truncate text-sm text-blue-700 hover:underline ${c.unread ? 'font-bold' : ''}`}>
                {c.subject}
              </Link>
              <span className="flex items-center gap-2 text-xs text-gray-600">
                <span className={`rounded px-1.5 py-0.5 font-semibold ${conversationStatusClass(c.status)}`}>{CONVERSATION_STATUS_LABELS[c.status]}</span>
                {formatWhen(c.lastMessageAt)}
              </span>
            </li>
          ))}
        </ul>
      )}
      {total > (rows?.length ?? 0) && (
        <Link to={inboxFilterPath(jobId ? { jobId } : { customerId: customer.id })} className="mt-2 inline-block text-sm font-semibold text-blue-700 hover:underline">
          See all {total} in the Inbox
        </Link>
      )}
    </section>
  );
}
